/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  FETCH_PRIMARY_RIDP,
  POST_SECONDARY_RIDP,
  FETCH_FARS_RESPONSE,
  POST_COMPLETE_FA_RS,
  POST_RBA_RIDP,
  POST_GET_RECORD_RESPONSE,
} from './actionTypes';

import {
  fetchPrimaryRIDPUtil,
  postSecondaryRIDPUtil,
  fetchFarsResponseUtil,
  postCompleteFARsUtil,
  postRbaRIDPUtil,
  postGetRecordResponseUtil,
} from './utils';

/**
 * Contains the action creator functions that update the RIDP slice of the store.
 *
 * @namespace RIDPActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/primary_RIDP
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof RIDPActions
   */
  static fetchPrimaryRIDP = (dispatch, callback) => {
    ReduxUtils.generateAction(dispatch, fetchPrimaryRIDPUtil, FETCH_PRIMARY_RIDP, callback);
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchPrimaryRIDP.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof RIDPActions
   */
  static resetFetchPrimaryRIDP = (dispatch, data) => {
    const json = {
      type: FETCH_PRIMARY_RIDP.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchPrimaryRIDP.
   *
   * @param {function} dispatch
   * @function
   * @memberof RIDPActions
   */
  static resetFetchPrimaryRIDPError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_PRIMARY_RIDP.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/secondary_RIDP
   *
   * @param {NewSDSecondaryRIDPRequestStruct} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof RIDPActions
   */
  static postSecondaryRIDP = (dispatch, newSDSecondaryRIDPRequestStruct, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postSecondaryRIDPUtil.bind(null, newSDSecondaryRIDPRequestStruct),
      POST_SECONDARY_RIDP,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postSecondaryRIDP.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof RIDPActions
   */
  static resetPostSecondaryRIDP = (dispatch, data) => {
    const json = {
      type: POST_SECONDARY_RIDP.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postSecondaryRIDP.
   *
   * @param {function} dispatch
   * @function
   * @memberof RIDPActions
   */
  static resetPostSecondaryRIDPError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_SECONDARY_RIDP.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/fars_response
   *
   * @param {string} dshReferenceNumber.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof RIDPActions
   */
  static fetchFarsResponse = (dispatch, dshReferenceNumber, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchFarsResponseUtil.bind(null, dshReferenceNumber),

      FETCH_FARS_RESPONSE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchFarsResponse.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof RIDPActions
   */
  static resetFetchFarsResponse = (dispatch, data) => {
    const json = {
      type: FETCH_FARS_RESPONSE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchFarsResponse.
   *
   * @param {function} dispatch
   * @function
   * @memberof RIDPActions
   */
  static resetFetchFarsResponseError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_FARS_RESPONSE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/complete_FARs
   *
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof RIDPActions
   */
  static postCompleteFARs = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postCompleteFARsUtil.bind(null, {}),
      POST_COMPLETE_FA_RS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postCompleteFARs.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof RIDPActions
   */
  static resetPostCompleteFARs = (dispatch, data) => {
    const json = {
      type: POST_COMPLETE_FA_RS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postCompleteFARs.
   *
   * @param {function} dispatch
   * @function
   * @memberof RIDPActions
   */
  static resetPostCompleteFARsError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_COMPLETE_FA_RS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/rba_RIDP
   *
   * @param {SDRIDPRequest} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof RIDPActions
   */
  static postRbaRIDP = (dispatch, sDRIDPRequest, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postRbaRIDPUtil.bind(null, sDRIDPRequest),
      POST_RBA_RIDP,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postRbaRIDP.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof RIDPActions
   */
  static resetPostRbaRIDP = (dispatch, data) => {
    const json = {
      type: POST_RBA_RIDP.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postRbaRIDP.
   *
   * @param {function} dispatch
   * @function
   * @memberof RIDPActions
   */
  static resetPostRbaRIDPError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_RBA_RIDP.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/getRecord_response
   *
   * @param {SDGetRecordRequest} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof RIDPActions
   */
  static postGetRecordResponse = (dispatch, sDGetRecordRequest, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postGetRecordResponseUtil.bind(null, sDGetRecordRequest),
      POST_GET_RECORD_RESPONSE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postGetRecordResponse.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof RIDPActions
   */
  static resetPostGetRecordResponse = (dispatch, data) => {
    const json = {
      type: POST_GET_RECORD_RESPONSE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postGetRecordResponse.
   *
   * @param {function} dispatch
   * @function
   * @memberof RIDPActions
   */
  static resetPostGetRecordResponseError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_GET_RECORD_RESPONSE.failureAction);
  };
}
